/**
 * SharedWorker de cache pour Altazion Commerce SDK.
 *
 * Exécuté dans un contexte SharedWorker partagé entre tous les onglets
 * du même domaine. Persiste les réponses API dans IndexedDB avec un TTL.
 *
 * Messages entrants (main thread → worker) :
 *   { id, type: 'GET', key }
 *   { id, type: 'SET', key, data, ttl }
 *   { id, type: 'INVALIDATE', pattern }
 *   { id, type: 'CLEAR_ALL' }
 *
 * Messages sortants (worker → main thread) :
 *   { id, result: T | null }
 *   { id, error: string }
 */

const DB_NAME = 'altazion-cache'
const DB_VERSION = 1
const STORE_NAME = 'entries'

interface CacheEntry {
  key: string
  data: unknown
  expiresAt: number
}

type IncomingMessage =
  | { id: string; type: 'GET'; key: string }
  | { id: string; type: 'SET'; key: string; data: unknown; ttl: number }
  | { id: string; type: 'INVALIDATE'; pattern: string }
  | { id: string; type: 'CLEAR_ALL' }

let db: IDBDatabase | null = null

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (db) { resolve(db); return }

    const request = indexedDB.open(DB_NAME, DB_VERSION)

    request.onupgradeneeded = (event) => {
      const target = event.target as IDBOpenDBRequest
      const database = target.result
      if (!database.objectStoreNames.contains(STORE_NAME)) {
        database.createObjectStore(STORE_NAME, { keyPath: 'key' })
      }
    }

    request.onsuccess = (event) => {
      db = (event.target as IDBOpenDBRequest).result
      resolve(db)
    }

    request.onerror = () => reject(request.error)
  })
}

async function getEntry(key: string): Promise<unknown | null> {
  const database = await openDb()
  return new Promise((resolve, reject) => {
    const tx = database.transaction(STORE_NAME, 'readonly')
    const req = tx.objectStore(STORE_NAME).get(key)

    req.onsuccess = () => {
      const entry = req.result as CacheEntry | undefined
      if (!entry) { resolve(null); return }
      if (Date.now() > entry.expiresAt) {
        // Entrée expirée — on la supprime en tâche de fond
        void deleteEntry(key)
        resolve(null)
      } else {
        resolve(entry.data)
      }
    }
    req.onerror = () => reject(req.error)
  })
}

async function setEntry(key: string, data: unknown, ttlMs: number): Promise<void> {
  const database = await openDb()
  return new Promise((resolve, reject) => {
    const entry: CacheEntry = { key, data, expiresAt: Date.now() + ttlMs }
    const tx = database.transaction(STORE_NAME, 'readwrite')
    const req = tx.objectStore(STORE_NAME).put(entry)
    req.onsuccess = () => resolve()
    req.onerror = () => reject(req.error)
  })
}

async function deleteEntry(key: string): Promise<void> {
  const database = await openDb()
  return new Promise((resolve, reject) => {
    const tx = database.transaction(STORE_NAME, 'readwrite')
    const req = tx.objectStore(STORE_NAME).delete(key)
    req.onsuccess = () => resolve()
    req.onerror = () => reject(req.error)
  })
}

async function invalidateByPattern(pattern: string): Promise<void> {
  const database = await openDb()
  const regex = new RegExp(pattern)
  return new Promise((resolve, reject) => {
    const tx = database.transaction(STORE_NAME, 'readwrite')
    const store = tx.objectStore(STORE_NAME)
    const keysRequest = store.getAllKeys()

    keysRequest.onsuccess = () => {
      const keys = keysRequest.result as string[]
      for (const key of keys) {
        if (regex.test(key)) {
          store.delete(key)
        }
      }
    }

    tx.oncomplete = () => resolve()
    tx.onerror = () => reject(tx.error)
  })
}

async function clearAll(): Promise<void> {
  const database = await openDb()
  return new Promise((resolve, reject) => {
    const tx = database.transaction(STORE_NAME, 'readwrite')
    const req = tx.objectStore(STORE_NAME).clear()
    req.onsuccess = () => resolve()
    req.onerror = () => reject(req.error)
  })
}

async function handleMessage(message: IncomingMessage, port: MessagePort): Promise<void> {
  try {
    switch (message.type) {
      case 'GET': {
        const result = await getEntry(message.key)
        port.postMessage({ id: message.id, result })
        break
      }
      case 'SET': {
        await setEntry(message.key, message.data, message.ttl)
        port.postMessage({ id: message.id, result: true })
        break
      }
      case 'INVALIDATE': {
        await invalidateByPattern(message.pattern)
        port.postMessage({ id: message.id, result: true })
        break
      }
      case 'CLEAR_ALL': {
        await clearAll()
        port.postMessage({ id: message.id, result: true })
        break
      }
    }
  } catch (err) {
    const message_ = err instanceof Error ? err.message : String(err)
    port.postMessage({ id: (message as { id: string }).id, error: message_ })
  }
}

// Entrée du SharedWorker
self.addEventListener('connect', (event: Event) => {
  const connectEvent = event as MessageEvent
  const port = connectEvent.ports[0]

  port.addEventListener('message', (e: MessageEvent<IncomingMessage>) => {
    void handleMessage(e.data, port)
  })

  port.start()
})
